import { useMemo } from 'react'
import { useRouter } from 'next/router'
import pick from 'lodash/pick'

import { sendError } from '@/utils/rollbar'
import useRoutes from '@/hooks/useRoutes'
import { QUERY_PARAMS, AUTHENTICATION_METHOD, INTERNAL_QUERY_PARAMS, SCOPE } from '@/constants'
import { getQueryParamValueAsString, toQS } from '@/utils/url'
import useScope from '@/hooks/useScope'
import config from '@/config'

export default function useAuth() {
  const router = useRouter()
  const routes = useRoutes()
  const { isDesktopClient, scope, isMobileClient } = useScope()

  const callbackUrl = getQueryParamValueAsString(QUERY_PARAMS.callbackUrl, router.query)
  const targetRoute = getQueryParamValueAsString(QUERY_PARAMS.targetRoute, router.query)
  const rememberMe = router.query[INTERNAL_QUERY_PARAMS.rememberMe] === 'true'
  const v3TokensEnabled = router.query[QUERY_PARAMS.tokenVersion] === '3'
  || isDesktopClient
  || router.pathname === routes.invitationsLogin.path
  || router.pathname === routes.createAccount.path

  // Needed since current scissors build passes showRememberMe=false
  const shouldShowRememberMe = router.query[QUERY_PARAMS.showRememberMe] === 'true' || (isDesktopClient && router.pathname === routes.login.path)

  let authenticationMethod = ''
  if (callbackUrl) {
    authenticationMethod = AUTHENTICATION_METHOD.CALLBACK_URL
  }

  const value = useMemo(() => ({
    isValid: !!authenticationMethod,
    handleRedirect: (params, useContinuePage = false) => {
      const modifiedParams = {
        ...params,
        ...targetRoute && { targetRoute },
        [INTERNAL_QUERY_PARAMS.rememberMe]: rememberMe,
      }

      let decodedCallbackUrl
      try {
        if (callbackUrl) {
          decodedCallbackUrl = decodeURIComponent(callbackUrl)
        }
      } catch (error) {
        sendError(error, `Unable to decode callbackUrl in handleRedirect: ${callbackUrl} `)
        decodedCallbackUrl = callbackUrl
      }

      if (useContinuePage) {
        router.push(routes.ssoContinue.url(modifiedParams))
      } else if (decodedCallbackUrl === routes.scanSnapAccounts.path) {
        router.push(routes.scanSnapAccounts.url(modifiedParams))
      } else if (isDesktopClient || isMobileClient) {
        router.push(routes.authenticated.url({
          [INTERNAL_QUERY_PARAMS.authenticatedRedirectUrl]: encodeURIComponent(`${decodedCallbackUrl}?${toQS(modifiedParams)}`),
          [INTERNAL_QUERY_PARAMS.fallbackAuthenticatedRedirectUrl]: encodeURIComponent(`${config.clients.jawn}/auth-return?${toQS({ ...modifiedParams, targetRoute: undefined })}`),
        }))
      } else {
        const [callbackUrlHost, callbackQueryParams] = decodedCallbackUrl.split('?')
        let redirectUrl = `${callbackUrlHost}?${toQS(modifiedParams)}`
        if (callbackQueryParams) redirectUrl = `${redirectUrl}${callbackQueryParams ? `&${callbackQueryParams}` : ''}`

        window.location.href = redirectUrl
      }
    },
    handleRememberMeChange: (nextValue) => {
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, [INTERNAL_QUERY_PARAMS.rememberMe]: nextValue },
      })
    },
    canStoreUsername: shouldShowRememberMe && (scope === SCOPE.APP_NEAT_COM || isDesktopClient || isMobileClient),
    rememberMe,
    shouldShowRememberMe,
    v3TokensEnabled,
    pickTokensData: (data) => pick(data, ['jwt', 'oauthToken', ...v3TokensEnabled ? ['refreshJWT', 'jwtExpiresAt', 'refreshJWTExpiresAt'] : []]),
    handleRedirectToMFA: (params) => router.push(routes.mfa.url(params)),
  }), [callbackUrl, authenticationMethod, rememberMe, router.pathname, isDesktopClient, isMobileClient, scope, shouldShowRememberMe, v3TokensEnabled])

  return value
}
